<template>
  <div class="route-planning-modal">
    <div v-if="loading" class="route-planning-modal__loading">
      <div class="route-planning-modal__map">
        <span class="route-planning-modal__pin route-planning-modal__pin--start"></span>
        <span class="route-planning-modal__pin route-planning-modal__pin--mid"></span>
        <span class="route-planning-modal__pin route-planning-modal__pin--end"></span>
        <span class="route-planning-modal__path"></span>
        <div class="route-planning-modal__truck">
          <BaseIcon name="truck" size="32" />
        </div>
      </div>
      <p class="route-planning-modal__loading-text">
        We berekenen de meest efficiënte volgorde op basis van adressen en reistijd.
      </p>
      <div class="route-planning-modal__progress">
        <span class="route-planning-modal__progress-bar"></span>
      </div>
    </div>

    <template v-else-if="preview">
      <div class="route-planning-modal__content">
        <div v-if="preview.summary" class="route-planning-modal__summary">
          <span>{{ preview.summary.work_order_count || 0 }} bonnen</span>
          <span v-if="preview.summary.total_duration_minutes">
            · {{ formatMinutes(preview.summary.total_duration_minutes) }} werk
          </span>
          <span v-if="preview.summary.travel_duration_minutes">
            · {{ formatMinutes(preview.summary.travel_duration_minutes) }} reistijd
          </span>
          <span v-if="preview.summary.warnings_count" class="route-planning-modal__warn-count">
            · {{ preview.summary.warnings_count }} waarschuwing(en)
          </span>
        </div>

        <div v-if="preview.warnings?.length" class="route-planning-modal__warnings">
          <div
            v-for="(w, idx) in preview.warnings"
            :key="'w-' + idx"
            class="route-planning-modal__warning"
            :class="'route-planning-modal__warning--' + (w.severity || 'warning')"
          >
            {{ w.message }}
          </div>
        </div>

        <div v-if="isRoutePreview && routePreviewCards.length" class="route-preview">
          <div v-for="route in routePreviewCards" :key="route.id" class="route-preview__card">
            <div class="route-preview__card-header">
              <div>
                <p class="route-preview__eyebrow">Route</p>
                <h4 class="route-preview__title">{{ route.label }}</h4>
              </div>
              <span class="route-preview__pill">{{ route.assignments.length }} stops</span>
            </div>

            <div class="route-preview__meta">
              <span v-if="route.durationLabel">{{ route.durationLabel }} werk</span>
              <span v-if="route.travelLabel">{{ route.travelLabel }} reistijd</span>
              <span v-if="route.distanceLabel">{{ route.distanceLabel }}</span>
            </div>

            <div class="route-preview__map">
              <div
                v-for="assignment in route.assignments"
                :key="assignment.work_order_id"
                class="route-preview-stop"
              >
                <span class="route-preview-stop__pin">{{ assignment.sequence }}</span>
                <div class="route-preview-stop__body">
                  <div class="route-preview-stop__top">
                    <span class="route-preview-stop__title">
                      {{ assignment.work_order_number || assignment.work_order_id }}
                      <template v-if="assignment.customer_name">
                        · {{ assignment.customer_name }}
                      </template>
                    </span>
                    <span class="route-preview-stop__time">
                      {{ formatAssignmentTime(assignment) }}
                    </span>
                  </div>
                  <span v-if="assignment.address_label" class="route-preview-stop__address">
                    {{ assignment.address_label }}
                  </span>
                  <span
                    v-if="assignment.travel_from_previous_minutes"
                    class="route-preview-stop__travel"
                  >
                    {{ assignment.travel_from_previous_minutes }} min rijden vanaf vorige stop
                  </span>
                  <span
                    v-if="windowBadge(assignment)"
                    class="route-preview-stop__window"
                    :class="`route-preview-stop__window--${windowBadge(assignment).tone}`"
                  >
                    <BaseIcon name="calendar-clock" size="11" />
                    {{ windowBadge(assignment).text }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="route-planning-modal__assignments">
          <div
            v-for="(a, idx) in previewAssignments"
            :key="a.work_order_id || idx"
            class="route-planning-modal__assignment"
          >
            <span class="route-planning-modal__seq">{{ a.sequence ?? idx + 1 }}</span>
            <div class="route-planning-modal__assignment-body">
              <span class="route-planning-modal__assignment-title">
                {{ a.work_order_number || a.work_order_id }}
                <template v-if="a.customer_name">— {{ a.customer_name }}</template>
              </span>
              <span class="route-planning-modal__assignment-meta">
                {{ formatAssignmentTime(a) }}
                <template v-if="a.assigned_to_label">· {{ a.assigned_to_label }}</template>
                <template v-if="a.travel_from_previous_minutes">
                  · {{ a.travel_from_previous_minutes }} min rijden
                </template>
              </span>
              <span v-if="a.address_label" class="route-planning-modal__assignment-address">
                {{ a.address_label }}
              </span>
              <span
                v-if="windowBadge(a)"
                class="route-preview-stop__window"
                :class="`route-preview-stop__window--${windowBadge(a).tone}`"
              >
                <BaseIcon name="calendar-clock" size="11" />
                {{ windowBadge(a).text }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="preview.unscheduled?.length" class="route-planning-modal__unscheduled">
          <p class="route-planning-modal__unscheduled-title">Niet ingepland in voorstel</p>
          <p
            v-for="u in preview.unscheduled"
            :key="u.work_order_id"
            class="route-planning-modal__unscheduled-item"
          >
            {{ u.work_order_id }} — {{ u.message || u.reason }}
          </p>
        </div>
      </div>

      <div class="route-planning-modal__footer">
        <div class="route-planning-modal__actions">
          <Btn variant="secondary" text="Annuleren" :disabled="applying" @click="onCancel" />
          <Btn
            variant="primary"
            text="Voorstel toepassen"
            icon="check"
            :loading="applying"
            :disabled="!canApply"
            @click="onApply"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import { computed } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@/components/partials/BaseIcon.vue';

  export default {
    name: 'RoutePlanningModal',
    components: { Btn, BaseIcon },
    props: {
      modalId: { type: Number, default: null },
      loading: { type: Boolean, default: true },
      applying: { type: Boolean, default: false },
      preview: { type: Object, default: null },
      onCancel: { type: Function, default: null },
      onApply: { type: Function, default: null },
    },
    setup(props) {
      const previewAssignments = computed(() => {
        if (!props.preview) return [];
        if (props.preview.assignments?.length) return props.preview.assignments;
        const fromRoutes = (props.preview.routes || []).flatMap((r) =>
          (r.assignments || []).map((a) => ({
            ...a,
            assigned_to_label: a.assigned_to_label || r.assigned_to_label,
          }))
        );
        if (fromRoutes.length) return fromRoutes;
        const fromDays = (props.preview.days || []).flatMap((d) =>
          (d.technicians || []).flatMap((t) =>
            (t.assignments || []).map((a) => ({
              ...a,
              assigned_to_label: a.assigned_to_label || t.assigned_to_label,
            }))
          )
        );
        return fromDays;
      });

      const isRoutePreview = computed(() => {
        return Array.isArray(props.preview?.routes) && props.preview.routes.length > 0;
      });

      const formatMinutes = (mins) => {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        if (h && m) return `${h}u ${m}m`;
        if (h) return `${h}u`;
        return `${m}m`;
      };

      const routePreviewCards = computed(() => {
        if (!isRoutePreview.value) return [];
        return props.preview.routes.map((route, routeIdx) => {
          const assignments = (route.assignments || []).map((assignment, assignmentIdx) => ({
            ...assignment,
            sequence: assignment.sequence ?? assignmentIdx + 1,
            assigned_to_label: assignment.assigned_to_label || route.assigned_to_label,
          }));

          return {
            id: route.id || route.route_id || `${route.assigned_to || 'route'}-${routeIdx}`,
            label:
              route.assigned_to_label ||
              route.technician_name ||
              route.label ||
              `Route ${routeIdx + 1}`,
            assignments,
            durationLabel: route.total_duration_minutes
              ? formatMinutes(route.total_duration_minutes)
              : null,
            travelLabel: route.travel_duration_minutes
              ? formatMinutes(route.travel_duration_minutes)
              : null,
            distanceLabel: route.distance_km ? `${Math.round(route.distance_km)} km` : null,
          };
        });
      });

      const hasBlockingWarnings = computed(() =>
        (props.preview?.warnings || []).some((w) => w.severity === 'blocking')
      );

      const canApply = computed(() => !!props.preview?.plan_id);

      const formatAssignmentTime = (a) => {
        if (!a.start) return '';
        const start = new Date(a.start);
        const end = a.end ? new Date(a.end) : null;
        const fmt = (d) =>
          d.toLocaleString('nl-NL', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          });
        return end
          ? `${fmt(start)} – ${end.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`
          : fmt(start);
      };

      // Klantvenster per stop: groen = past, oranje = buiten voorkeur (bellen),
      // rood = buiten hard venster (zou niet mogen voorkomen, maar toon het wel).
      const windowBadge = (a) => {
        if (!a?.planning_window_label) return null;
        const label = a.planning_window_label;
        if (a.window_match === 'inside') return { tone: 'ok', text: `Past: ${label}` };
        if (a.window_match === 'outside') {
          const windowWarning = (a.warnings || []).find(
            (w) => w.code === 'outside_customer_window'
          );
          return {
            tone: a.window_hard ? 'blocked' : 'warn',
            text: windowWarning?.message || `Buiten voorkeur (${label})`,
          };
        }
        return { tone: 'neutral', text: label };
      };

      return {
        previewAssignments,
        isRoutePreview,
        routePreviewCards,
        hasBlockingWarnings,
        canApply,
        formatMinutes,
        formatAssignmentTime,
        windowBadge,
      };
    },
  };
</script>

<style scoped lang="scss">
  .route-planning-modal {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    max-height: min(calc(100vh - 10rem), 720px);

    &__loading {
      padding: 0.5rem 0 0.25rem;
      text-align: center;
      max-width: 300px;
      margin: 0 auto;
      width: 100%;
    }

    &__map {
      position: relative;
      height: 10rem;
      max-width: 300px;
      width: 100%;
      margin: 0 auto 1.25rem;
      border-radius: 0.875rem;
      background:
        radial-gradient(circle at 18% 24%, rgba(59, 130, 246, 0.14), transparent 28%),
        radial-gradient(circle at 82% 72%, rgba(16, 185, 129, 0.14), transparent 30%),
        linear-gradient(180deg, var(--brand-50), var(--slate-50));
      overflow: hidden;
    }

    &__path {
      position: absolute;
      left: 2.5rem;
      right: 2.5rem;
      top: 50%;
      height: 3px;
      background: linear-gradient(90deg, var(--brand-300), var(--brand-600));
      transform: translateY(-50%) rotate(8deg);
      transform-origin: center;
      border-radius: 999px;
    }

    &__pin {
      position: absolute;
      z-index: 1;
      width: 1rem;
      height: 1rem;
      border-radius: 999px;
      background: var(--brand-600);
      box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.16);

      &--start {
        left: 2rem;
        top: 3.125rem;
      }

      &--mid {
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        animation: routeOverlayPulse 1.6s ease-in-out infinite;
      }

      &--end {
        right: 2rem;
        bottom: 3.125rem;
        background: var(--green-500);
        box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.16);
      }
    }

    &__truck {
      position: absolute;
      left: 2rem;
      top: calc(50% - 1rem);
      z-index: 2;
      color: var(--slate-700);
      animation: routeOverlayDrive 4s cubic-bezier(0.5, 1, 0.3, 1) infinite;

      :deep(.icon) {
        display: flex;
      }

      :deep(svg) {
        fill: currentColor;
      }
    }

    &__loading-text {
      margin: 0 0 1.25rem;
      font-size: 0.9375rem;
      line-height: 1.5;
      color: var(--slate-600);
    }

    &__progress {
      height: 0.25rem;
      border-radius: 999px;
      background: var(--slate-100);
      overflow: hidden;
      max-width: 300px;
      margin: 0 auto;
    }

    &__progress-bar {
      display: block;
      height: 100%;
      width: 35%;
      border-radius: inherit;
      background: var(--brand-600);
      animation: routeOverlayProgress 2.4s ease-in-out infinite;
    }

    &__content {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding-right: 0.125rem;
    }

    &__summary {
      font-size: 0.8125rem;
      color: var(--slate-600);
      margin-bottom: 0.75rem;
    }

    &__warn-count {
      color: var(--amber-700);
    }

    &__warnings {
      margin-bottom: 0.75rem;
    }

    &__warning {
      font-size: 0.8125rem;
      padding: 0.5rem 0.625rem;
      border-radius: 0.375rem;
      margin-bottom: 0.375rem;

      &--warning {
        background: var(--amber-50);
        color: var(--amber-800);
      }

      &--blocking {
        background: var(--red-50);
        color: var(--red-800);
      }

      &--info {
        background: var(--slate-100);
        color: var(--slate-700);
      }
    }

    &__assignments {
      margin-bottom: 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    &__assignment {
      display: flex;
      gap: 0.5rem;
      padding: 0.625rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      border-radius: 0.375rem;
      font-size: 0.8125rem;
    }

    &__seq {
      flex-shrink: 0;
      width: 1.375rem;
      height: 1.375rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background: white;
      border: 1px solid var(--slate-200);
      border-radius: 9999px;
      font-weight: 600;
      color: var(--slate-600);
    }

    &__assignment-body {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
    }

    &__assignment-title {
      font-weight: 600;
      color: var(--slate-800);
    }

    &__assignment-meta,
    &__assignment-address {
      color: var(--slate-500);
      font-size: 0.75rem;
    }

    &__unscheduled {
      margin-bottom: 0.75rem;
      padding: 0.625rem;
      background: var(--amber-50);
      border-radius: 0.375rem;
      font-size: 0.8125rem;
    }

    &__unscheduled-title {
      margin: 0 0 0.25rem;
      font-weight: 600;
      color: var(--amber-800);
    }

    &__unscheduled-item {
      margin: 0;
      color: var(--amber-700);
    }

    &__footer {
      flex-shrink: 0;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-200);
      margin-top: 0.25rem;
    }

    &__apply-hint {
      margin: 0 0 0.625rem;
      font-size: 0.75rem;
      line-height: 1.4;
      color: var(--amber-800);
    }

    &__actions {
      display: flex;
      gap: 0.5rem;
      justify-content: flex-end;
    }

    .route-preview {
      margin-bottom: 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;

      &__card {
        background: var(--slate-50);
        border: 1px solid var(--slate-200);
        border-radius: 0.75rem;
        padding: 0.75rem;
      }

      &__card-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: 0.5rem;
      }

      &__eyebrow {
        margin: 0 0 0.125rem;
        color: var(--slate-400);
        font-size: 0.625rem;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }

      &__title {
        margin: 0;
        color: var(--slate-900);
        font-size: 0.9375rem;
        font-weight: 700;
      }

      &__pill {
        flex-shrink: 0;
        background: var(--brand-50);
        color: var(--brand-700);
        border: 1px solid var(--brand-100);
        border-radius: 999px;
        padding: 0.1875rem 0.5rem;
        font-size: 0.6875rem;
        font-weight: 700;
      }

      &__meta {
        display: flex;
        flex-wrap: wrap;
        gap: 0.375rem;
        margin-bottom: 0.75rem;

        span {
          background: white;
          color: var(--slate-600);
          border: 1px solid var(--slate-200);
          border-radius: 999px;
          padding: 0.1875rem 0.5rem;
          font-size: 0.6875rem;
          font-weight: 600;
        }
      }

      &__map {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
    }

    .route-preview-stop {
      position: relative;
      display: flex;
      gap: 0.625rem;

      &:not(:last-child)::after {
        content: '';
        position: absolute;
        left: 0.6875rem;
        top: 1.625rem;
        bottom: -0.625rem;
        width: 2px;
        background: var(--slate-200);
      }

      &__pin {
        position: relative;
        z-index: 1;
        flex-shrink: 0;
        width: 1.5rem;
        height: 1.5rem;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: var(--slate-900);
        color: white;
        font-size: 0.6875rem;
        font-weight: 700;
      }

      &__body {
        flex: 1;
        min-width: 0;
        padding-bottom: 0.5rem;
      }

      &__top {
        display: flex;
        justify-content: space-between;
        gap: 0.75rem;
      }

      &__title {
        min-width: 0;
        color: var(--slate-900);
        font-size: 0.8125rem;
        font-weight: 700;
        line-height: 1.25;
      }

      &__time {
        flex-shrink: 0;
        color: var(--slate-500);
        font-size: 0.6875rem;
        font-weight: 600;
        white-space: nowrap;
      }

      &__address,
      &__travel {
        display: block;
        margin-top: 0.125rem;
        color: var(--slate-500);
        font-size: 0.75rem;
        line-height: 1.3;
      }

      &__travel {
        color: var(--brand-700);
      }

      &__window {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        margin-top: 0.25rem;
        padding: 0.125rem 0.5rem;
        border-radius: 999px;
        font-size: 0.6875rem;
        font-weight: 600;
        line-height: 1.3;
        background: var(--slate-100);
        color: var(--slate-700);

        &--ok {
          background: var(--green-50);
          color: var(--green-700);
        }
        &--warn {
          background: var(--amber-50);
          color: var(--amber-700);
        }
        &--blocked {
          background: var(--red-50);
          color: var(--red-700);
        }
      }
    }
  }

  @keyframes routeOverlayDrive {
    0% {
      transform: translate(0, -0.75rem) rotate(8deg);
      opacity: 0;
    }
    12%,
    82% {
      opacity: 1;
    }
    100% {
      transform: translate(10rem, 0.75rem) rotate(8deg);
      opacity: 0;
    }
  }

  @keyframes routeOverlayPulse {
    0%,
    100% {
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      transform: translate(-50%, -50%) scale(1.35);
    }
  }

  @keyframes routeOverlayProgress {
    0% {
      transform: translateX(-120%);
    }
    100% {
      transform: translateX(320%);
    }
  }
</style>
